'use client'

import { usePathname } from 'next/navigation'
import { ArrowLeft, ChevronRight, Link2 } from 'lucide-react'
import { formatDateNormal, formatImageURL } from '@/utils/util'
import { getReadingTime } from '@/utils/blog'
import { getFacebookShareUrl, getTwitterShareUrl, openSharePopup, copyToClipboard } from '@/utils/share'
import { navigateUrl } from '@/utils/client-request'
import { toast } from '@/lib/toast'
import BlogCard from './BlogCard'

export default function BlogDetail({ blog, related = [], vendor }: { blog: any, related?: any[], vendor?: any }) {
  const pathname = usePathname() || ''
  const date = blog?.createdAt ? formatDateNormal(new Date(blog.createdAt)) : ''
  const mins = getReadingTime(blog?.content || '')
  const vendorName = vendor?.name || 'Shop.mn'
  const next = (related || []).find((p: any) => p.id !== blog?.id) || (related || [])[0]

  const onShareFacebook = () => openSharePopup(getFacebookShareUrl(window.location.href))
  const onShareTwitter = () => openSharePopup(getTwitterShareUrl(window.location.href, blog?.title || ''))
  const onCopyLink = async () => {
    const ok = await copyToClipboard(window.location.href)
    if (ok) toast.success('Холбоос хуулагдлаа')
    else toast.error('Хуулж чадсангүй')
  }

  return (
    <section className="bg-cream pt-6 pb-16">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10">
        <a href={navigateUrl('/blog', pathname)} className="inline-flex items-center gap-1.5 text-[13px] text-foreground/55 hover:text-foreground transition-colors">
          <ArrowLeft className="size-3.5" /> Блог руу буцах
        </a>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/55">
              {vendorName} · Нийтлэл
            </div>
            <h1 className="mt-4 font-bold font-gilroy tracking-[-0.025em] leading-[1.0] text-foreground" style={{ fontSize: 'clamp(2rem, 4.6vw, 4rem)' }}>
              {blog?.title}
            </h1>
            <div className="mt-5 flex items-center gap-3 text-[13px] text-foreground/60">
              <span>{date}</span>
              {date && mins ? <span className="w-1 h-1 rounded-full bg-foreground/30" /> : null}
              {mins ? <span>{mins} мин унших</span> : null}
            </div>
          </div>
        </div>

        <div className="mt-8 relative rounded-3xl overflow-hidden aspect-[16/9] bg-[#F1EEE7]">
          <img src={formatImageURL(blog?.thumbnail || 'placeholder.png')} alt={blog?.title} className="absolute inset-0 w-full h-full object-cover" />
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <article className="lg:col-span-8 lg:col-start-2 max-w-[720px]">
            <div className="doc-prose text-[16px] sm:text-[17px] leading-[1.75] text-foreground/85" dangerouslySetInnerHTML={{ __html: blog?.content || '' }} />
          </article>

          <aside className="lg:col-span-3 lg:sticky lg:top-[120px] lg:self-start">
            <div className="rounded-2xl bg-white border border-foreground/10 p-5">
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/55 mb-3">Хуваалцах</div>
              <div className="flex flex-wrap gap-2">
                <button onClick={onShareFacebook} className="h-9 px-3 inline-flex items-center gap-1.5 rounded-full border border-foreground/10 text-[12px] font-semibold text-foreground/60 hover:text-foreground hover:border-foreground/30 transition-colors cursor-pointer">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 8H6v4h3v12h5V12h3.6l.4-4H14V6.3c0-1 .2-1.3 1.2-1.3H18V0h-3.6C10.6 0 9 1.7 9 4.9V8z"/></svg>
                  Facebook
                </button>
                <button onClick={onShareTwitter} className="h-9 px-3 inline-flex items-center gap-1.5 rounded-full border border-foreground/10 text-[12px] font-semibold text-foreground/60 hover:text-foreground hover:border-foreground/30 transition-colors cursor-pointer">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25h6.81l4.713 6.231 5.467-6.231z"/></svg>
                  X
                </button>
                <button onClick={onCopyLink} className="h-9 px-3 inline-flex items-center gap-1.5 rounded-full border border-foreground/10 text-[12px] font-semibold text-foreground/60 hover:text-foreground hover:border-foreground/30 transition-colors cursor-pointer">
                  <Link2 className="size-3.5" /> Линк
                </button>
              </div>
            </div>
          </aside>
        </div>

        {related && related.length > 0 && (
          <div className="mt-20">
            <div className="flex items-end justify-between gap-4 mb-7">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/55">Үргэлжлүүлэн унших</div>
                <h2 className="mt-1.5 font-bold font-gilroy tracking-[-0.015em] leading-[1.05] text-foreground" style={{ fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)' }}>
                  Танд таалагдаж болох нийтлэлүүд
                </h2>
              </div>
              <a href={navigateUrl('/blog', pathname)} className="hidden sm:inline-flex items-center gap-1 text-[13px] text-foreground/55 hover:text-foreground transition-colors">
                Блогт буцах <ChevronRight className="size-3.5" />
              </a>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-7 gap-y-12">
              {related.filter((p: any) => p.id !== blog?.id).slice(0, 3).map((p: any) => (
                <BlogCard key={p.id} blog={p} />
              ))}
            </div>
          </div>
        )}

        {next && (
          <a href={navigateUrl(`/blog/${next.id}`, pathname)} className="mt-20 group block relative rounded-3xl overflow-hidden cursor-pointer aspect-[16/6] bg-foreground">
            <img src={formatImageURL(next.thumbnail || 'placeholder.png')} alt={next.title} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-70 transition-opacity" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/20" />
            <div className="absolute inset-0 p-6 sm:p-12 flex flex-col justify-center text-white">
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/70">Дараагийн нийтлэл</div>
              <h3 className="mt-2 font-bold font-gilroy tracking-[-0.02em] leading-[1.05] max-w-3xl" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.5rem)' }}>
                {next.title}
              </h3>
              <div className="mt-4 inline-flex items-center gap-2 text-[13px] font-semibold group-hover:gap-3 transition-all">
                Унших <ChevronRight className="size-3.5" />
              </div>
            </div>
          </a>
        )}
      </div>
    </section>
  )
}
